
import { defineComponent, PropType } from "vue";
import { useI18n } from "vue-i18n";
import { mapGetters, mapState } from "vuex";
import { RootGetters } from "@/store/getters";
import { ISlide, TechnicalScreeningClasses } from "@/models/slide";
import SlideImg from "./Image.component.vue";
import { AlertResolution, AlertType } from "@/modules/slides/models/slide.models";
import { BFF_BASE_PATH } from "@/core/utils";
import ILabel from "@/components/label/ILabel.vue";
import DeleteSlideDialog from "./DeleteSlideDialog.vue";
import { SlideDeletion } from "../models/slide.models";
import { ActiveView } from "@/models/active-view";
import IbIcons from "@/components/icons/ib-icons.vue";

export default defineComponent({
  setup() {
    const { t, n } = useI18n({
      inheritLocale: true,
      useScope: "local",
    });
    return { t, n, ActiveView };
  },
  name: "SlidePreview",
  data() {
    return {
      AlertType,
      AlertResolution,
      deleteDialog: false,
      BFF_BASE_PATH,
    };
  },
  components: {
    IbIcons,
    ILabel,
    SlideImg,
    DeleteSlideDialog,
  },
  props: {
    slide: {
      type: Object as PropType<ISlide>,
      required: true,
    },
    theme: {
      type: String,
      default: "light",
    },
  },
  computed: {
    ...mapState(["activeSlideId", "loggedInUserName", "activeView"]),
    ...mapGetters({
      isFirstRead: RootGetters.IS_FIRST_READ,
      isSecondRead: RootGetters.IS_SECOND_READ,
      isDirectLink: RootGetters.GET_IS_DIRECT_LINK,
      isCompleted: RootGetters.GET_IS_SLIDE_COMPLETED,
      globals: RootGetters.GET_GLOBALS,
      config: RootGetters.CONFIG,
      getSlideLabels: RootGetters.GET_SLIDE_LABELS,
      activeCaseTissue: RootGetters.GET_ACTIVE_CASE_TISSUE,
    }),
    tissue() {
      return this.slide.subTissue || this.activeCaseTissue;
    },
    slideLabels() {
      return this.getSlideLabels(this.slide.slideId);
    },
    slideThumbnail() {
      return `${BFF_BASE_PATH}/get_thumbnail_jpeg/${encodeURIComponent(this.slide.slideId)}/202/98`;
    },
    isAllowedUser() {
      return this.loggedInUserName.toLowerCase() === SlideDeletion.ADMIN;
    },
    showSlideDeletion() {
      // Showing the slide deletion functionality if the follwing terms are met:
      // 1. Not currently in the viewer (meaning, there's an active slide)
      // 2. Users that are allowed to perform the delete operation (admins)
      // 3. Not in the case-report modal
      // 4. configuration for the feature is enabled
      return (
        !this.activeSlideId &&
        this.isAllowedUser &&
        this.activeView !== ActiveView.CaseReport &&
        this.config.enable_slide_deletion
      );
    },
  },
  methods: {
    selectSlide(slide: ISlide, alertIndex: number) {
      this.$emit("selectFromSlide", { slide, alertIndex });
    },
    hasNoThumbs(thumbs: { label?: string; combo?: string; macro?: string; thumbnail?: string }) {
      return !thumbs.combo && !thumbs.label && !thumbs.macro && !thumbs.thumbnail;
    },
    isMacroNoLabel(slide: ISlide) {
      return (slide.thumbs.macro || slide.thumbs.thumbnail) && !slide.thumbs.label;
    },
    isMacroLabel(slide: ISlide) {
      return (slide.thumbs.macro || slide.thumbs.thumbnail) && slide.thumbs.label;
    },
    isTechnicalIssue(slide: ISlide) {
      return TechnicalScreeningClasses.includes(slide.screeningClass);
    },
  },
});
